import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X, Search } from "lucide-react";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { useStore } from "@/context/StoreContext";

const SORTS = [
  { key: "popular", label: "Popularity" },
  { key: "price-low", label: "Price: Low to High" },
  { key: "price-high", label: "Price: High to Low" },
  { key: "rating", label: "Top Rated" },
];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const { wishlist } = useStore();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const category = params.get("category") || "all";
  const search = params.get("search") || "";
  const badge = params.get("badge") || "";
  const sort = params.get("sort") || "popular";
  const wishlistOnly = params.get("wishlist") === "1";
  const [searchInput, setSearchInput] = useState(search);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    api
      .get("/products", { params: { category, search, badge, sort } })
      .then((r) => {
        let data = r.data;
        if (wishlistOnly) data = data.filter((p) => wishlist.includes(p.slug));
        setProducts(data);
      })
      .finally(() => setLoading(false));
  }, [category, search, badge, sort, wishlistOnly]); // eslint-disable-line

  const update = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === null || v === "" || v === "all") next.delete(k);
      else next.set(k, v);
    });
    setParams(next);
  };

  const title = wishlistOnly
    ? "My Wishlist"
    : badge
    ? { bestseller: "Best Sellers", deal: "Today's Deals", "kashmir-special": "Kashmir Special", giftbox: "Gift Boxes", combo: "Combo Offers", festive: "Festive Offers", premium: "Premium" }[badge] || "Shop"
    : category !== "all"
    ? categories.find((c) => c.slug === category)?.label || "Shop"
    : "All Products";

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
      <div className="mb-6">
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#1F130B]" data-testid="shop-title">{title}</h1>
        <p className="text-sm text-[#6B5B52] mt-1">{loading ? "Loading..." : `${products.length} product${products.length !== 1 ? "s" : ""} found`}</p>
      </div>

      {/* search + sort bar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <form
          onSubmit={(e) => { e.preventDefault(); update({ search: searchInput || null }); }}
          className="relative flex-1"
        >
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            data-testid="shop-search-input"
            placeholder="Search products..."
            className="w-full rounded-full border border-[#E7E0D6] bg-white pl-9 pr-4 py-2.5 text-sm outline-none focus:border-[#C2410C]"
          />
        </form>
        <select
          value={sort}
          onChange={(e) => update({ sort: e.target.value })}
          data-testid="shop-sort-select"
          className="rounded-full border border-[#E7E0D6] bg-white px-4 py-2.5 text-sm outline-none focus:border-[#C2410C]"
        >
          {SORTS.map((s) => <option key={s.key} value={s.key}>Sort: {s.label}</option>)}
        </select>
        <button onClick={() => setShowFilters((s) => !s)} className="lg:hidden inline-flex items-center justify-center gap-2 rounded-full border border-[#E7E0D6] bg-white px-4 py-2.5 text-sm font-semibold" data-testid="shop-filter-toggle">
          <SlidersHorizontal size={16} /> Categories
        </button>
      </div>

      <div className="grid lg:grid-cols-[220px_1fr] gap-8">
        {/* sidebar */}
        <aside className={`${showFilters ? "block" : "hidden"} lg:block`}>
          <div className="rounded-2xl bg-white border border-[#E7E0D6] p-4 sticky top-28">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-lg font-semibold text-[#1F130B]">Categories</h3>
              {(category !== "all" || badge) && (
                <button onClick={() => update({ category: null, badge: null })} className="text-xs text-[#C2410C] flex items-center gap-1" data-testid="clear-filters"><X size={12} /> Clear</button>
              )}
            </div>
            <button
              onClick={() => { update({ category: null, badge: null }); setShowFilters(false); }}
              data-testid="cat-filter-all"
              className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium ${category === "all" && !badge ? "bg-[#FEF3C7] text-[#9A3412]" : "text-[#6B5B52] hover:bg-[#FAF6F0]"}`}
            >All Products</button>
            {categories.map((c) => (
              <button
                key={c.slug}
                onClick={() => { update({ category: c.slug, badge: null }); setShowFilters(false); }}
                data-testid={`cat-filter-${c.slug}`}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium ${category === c.slug ? "bg-[#FEF3C7] text-[#9A3412]" : "text-[#6B5B52] hover:bg-[#FAF6F0]"}`}
              >{c.label}</button>
            ))}
          </div>
        </aside>

        {/* grid */}
        <div>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => <div key={i} className="rounded-2xl bg-white border border-[#E7E0D6] h-96 animate-pulse" />)}
            </div>
          ) : products.length === 0 ? (
            <div className="rounded-2xl bg-white border border-[#E7E0D6] p-16 text-center">
              <p className="text-[#6B5B52]">No products found. Try a different search or category.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="shop-products-grid">
              {products.map((p) => <ProductCard key={p.slug} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
